import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, Calendar, MapPin, Tag, Lock, Globe as GlobeIcon, Loader2, Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * CompanyEventsTimeline
 * Mostra i B2B Events di una specifica azienda (host o partecipante)
 * in ordine cronologico crescente. Vertical stepper layout.
 */
export default function CompanyEventsTimeline({ companyName }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyName) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/network/events/by-company`, {
          params: { name: companyName },
          withCredentials: true,
        });
        setEvents(Array.isArray(data) ? data : []);
      } catch {
        setEvents([]);
      } finally { setLoading(false); }
    })();
  }, [companyName]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-[#A0AEC0]">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carico eventi B2B…
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <p className="text-xs text-[#A0AEC0] italic py-3">
        Nessun evento B2B associato a questa azienda.
      </p>
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="relative pl-4">
      {/* vertical line */}
      <div className="absolute left-1.5 top-1 bottom-1 w-px bg-[#A78BFA]/40" />
      <div className="space-y-4">
        {events.map((ev) => {
          const isFuture = ev.date && ev.date >= today;
          const isHost = ev.role_for_company === "Host";
          const isPrivate = (ev.visibility || "").toLowerCase().startsWith("priv");
          const VIcon = isPrivate ? Lock : GlobeIcon;
          const vColor = isPrivate ? "#B45309" : "#059669";
          return (
            <div key={ev.id} data-testid="company-event-timeline-item" className="relative pl-4">
              {/* dot */}
              <span
                className={`absolute -left-[9px] top-1 w-2.5 h-2.5 rounded-full border-2 ${
                  isFuture ? "bg-[#A78BFA] border-[#A78BFA]" : "bg-[#0D0F12] border-[#A78BFA]"
                }`}
              />
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-white truncate" title={ev.name}>
                      {ev.name}
                    </p>
                    {isHost && (
                      <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 border border-[#A78BFA]/60 bg-[#A78BFA]/15 text-[#A78BFA] font-bold tracking-widest uppercase rounded-sm">
                        <Award className="w-2.5 h-2.5" /> HOST
                      </span>
                    )}
                    {!isHost && ev.role_for_company && (
                      <span className="inline-flex text-[9px] px-1.5 py-0.5 border border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF] font-mono tracking-widest uppercase rounded-sm">
                        {ev.role_for_company}
                      </span>
                    )}
                    <span
                      className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 border font-bold tracking-widest uppercase rounded-sm"
                      style={{ color: vColor, borderColor: `${vColor}66`, background: `${vColor}15` }}
                    >
                      <VIcon className="w-2.5 h-2.5" />
                      {ev.visibility || "Pubblico"}
                    </span>
                    {isFuture && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-[#A78BFA] text-black font-bold tracking-widest uppercase rounded-sm">
                        Futuro
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-[11px] text-[#CBD5E1]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-[#A78BFA]" /> {ev.date}
                    </span>
                    {ev.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-[#A78BFA]" /> {ev.location}
                      </span>
                    )}
                    {ev.sector && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3 text-[#A78BFA]" /> {ev.sector}
                      </span>
                    )}
                    {!isHost && ev.host_company && (
                      <span className="text-[#A0AEC0]">Host: <span className="text-white">{ev.host_company}</span></span>
                    )}
                    <span className="flex items-center gap-1 text-[#A0AEC0]">
                      <Users className="w-3 h-3" /> {ev.attendees_count}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
